import { buildEndpoint, DEFAULT_BASE_URL } from './endpoint';

const STORAGE_PREFIX = 'v14_ai_';

/**
 * The two default model names, used whenever no model is picked in the UI. The
 * mode picks which: Optimize → fast (a non-reasoning instruct model), Review /
 * Draft / Memo → reasoning.
 *
 * These are gateway ALIASES from the `v14-llm-gateway` config, not provider
 * model ids, so the underlying model can be re-pointed there without touching
 * the add-in. The Word-Add-In team's key is scoped to FOUR aliases:
 * deepseek-flash (v4-flash-0731), qwen-3.8-next, glm-5.3, and gemini-flash
 * (gemini-3.8-flash via Cortecs, added 2026-09-18). Naming anything else here
 * produces a 403 "team not allowed to access model" for every user — the team
 * scope has to be widened FIRST, then this list.
 *
 * 2026-09-25: the gateway retired the `GLM 5.3 Thinking Low/High/Max` aliases.
 * Reasoning depth is a per-request parameter now — the gateway forwards
 * `reasoning_effort` (none | low | high | max) to TensorX's GLM 5.3. The
 * reasoning tasks therefore name the plain alias and send
 * {@link WORDADDIN_REASONING_EFFORT}, which reproduces what the retired
 * "Thinking High" alias pinned server-side.
 *
 * They used to be `WordAddIn_Fast` / `WordAddIn_Reasoning`, which were DELETED
 * from the gateway in that redesign — so every request that fell back to a
 * default was naming a model the gateway no longer knows. The names below are a
 * client-side setting by design; the gateway README says so explicitly.
 */
export const WORDADDIN_FAST = 'deepseek-flash';
export const WORDADDIN_REASONING = 'glm-5.3';

/**
 * 2026-09-30: the gateway aliases got whitespace-free ids (OpenWebUI 0.11.4 refuses
 * model ids with spaces). The gateway still answers the old names as hidden aliases,
 * so a choice saved before the rename keeps working; {@link normalizeModelId} moves
 * it to the new id when it is read back, and {@link modelLabel} keeps showing the
 * familiar names in the pickers (the gateway's `/models` returns ids only).
 */
export const LEGACY_MODEL_IDS: Record<string, string> = {
  'DeepSeek Flash': 'deepseek-flash',
  'DeepSeek Flash New 4.1': 'deepseek-flash-new-4.1',
  'Qwen Large': 'qwen-large',
  'Qwen Small': 'qwen-small',
  'Qwen 3.8 Next': 'qwen-3.8-next',
  'GLM 5.3': 'glm-5.3',
  'GLM 5.3 Flash': 'glm-5.3-flash',
  'Gemini Flash': 'gemini-flash',
  'Gemini Flash Lite': 'gemini-flash-lite',
};
const MODEL_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(LEGACY_MODEL_IDS).map(([label, id]) => [id, label]),
);

/** The current gateway id for a model id saved before the 2026-09-30 rename. */
export function normalizeModelId(id: string): string {
  return LEGACY_MODEL_IDS[id] ?? id;
}

/** The name shown for a gateway model id ("glm-5.3" -> "GLM 5.3"); other ids as-is. */
export function modelLabel(id: string): string {
  return MODEL_LABELS[id] ?? id;
}
/** Sent with every request on {@link WORDADDIN_REASONING}; a picked model omits it. */
export const WORDADDIN_REASONING_EFFORT = 'high';
const MODELS_CACHE_KEY = `${STORAGE_PREFIX}models`;
/** Cached model list is considered fresh for this long. */
const MODELS_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

export interface AiSettings {
  /**
   * Base URL of an OpenAI-compatible API, up to and including the version
   * segment. Point this at your LiteLLM gateway (e.g. `https://gateway/v1`),
   * which routes to Scaleway/OpenAI/etc. and holds the provider keys.
   */
  baseUrl: string;
  /** Bearer token — for a LiteLLM gateway this is the virtual key. */
  apiKey: string;
  model: string;
  /**
   * Show the gateway's routing aliases (bare names like `smart`/`fast`) in the
   * model list alongside the real `<provider>/<id>` models. Off by default — we
   * curate the list via the key's model access and pick concrete models.
   */
  includeAliases: boolean;
  /**
   * Analysis depth for reasoning models:
   * - 'fast' (default) — suppress the model's internal chain-of-thought
   *   (`enable_thinking:false`). On the default Qwen 3.5 397B this cuts a
   *   ~25k-char hidden reasoning trace, taking a rewrite from ~60s to ~2s with
   *   no loss in edit quality (measured). Harmless on non-reasoning models.
   * - 'thorough' — allow full step-by-step reasoning. Slower (tens of seconds)
   *   but can help complex multi-clause consistency edits (investor/company
   *   review with follow-on changes).
   */
  thinkingMode: 'fast' | 'thorough';
}

/** A few well-known OpenAI-compatible endpoints to offer as presets. */
export const BASE_URL_PRESETS: { label: string; url: string }[] = [
  { label: 'V14 Gateway', url: 'https://gateway.v14.tools/v1' },
  { label: 'Scaleway (Projekt-URL eintragen)', url: 'https://api.scaleway.ai/v1' },
  { label: 'OpenAI', url: 'https://api.openai.com/v1' },
];

export interface AiModel {
  id: string;
  label: string;
}

/**
 * Fallback model list used only when the gateway's `/models` endpoint can't be
 * reached (offline, no key yet, gateway down). We target the real provider
 * models directly (`<provider>/<id>`), not the routing aliases. The live list
 * from {@link fetchModels} is preferred and supersedes this.
 */
export const FALLBACK_MODELS: AiModel[] = [
  // Shown only when `/models` cannot be reached (offline, no key yet, gateway
  // down). These are the gateway's use-case ALIASES rather than provider ids —
  // the previous list named `scaleway/*` ids that the alias redesign left
  // pointing nowhere useful, so a keyless pane offered four models it could not
  // actually call.
  //
  // Default [0] is a fast NON-reasoning model: on a one-clause simplify the
  // reasoning models cost tens of seconds because they emit a large hidden
  // chain-of-thought first. They stay selectable for deliberate deep review.
  { id: 'deepseek-flash', label: 'DeepSeek Flash' },
  { id: 'qwen-3.8-next', label: 'Qwen 3.8 Next' },
  { id: 'glm-5.3', label: 'GLM 5.3' },
  { id: 'gemini-flash', label: 'Gemini Flash' },
];

/**
 * The gateway's aliases are bare names (no provider prefix); the raw per-provider
 * catalogue is `<provider>/<id>`. `includeAliases` (off by default) controls
 * whether the aliases are shown alongside the raw catalogue.
 *
 * Note the meaning of that setting has aged: it was written when the aliases were
 * two internal routing names worth hiding. Since the 2026-08-23 gateway redesign
 * the eight use-case aliases ARE the curated surface, and the raw catalogue is
 * the admin-only layer — so the filter is close to backwards. It survives because
 * the only UI consumer (`useModelPicker`) uses the unfiltered `getAllChatModels`.
 */
function isRoutingAlias(id: string): boolean {
  return !id.includes('/');
}

/** Applies the alias visibility setting to a model list. */
function applyAliasFilter(models: AiModel[]): AiModel[] {
  if (getAiSettings().includeAliases) return models;
  return models.filter((m) => !isRoutingAlias(m.id));
}

/** @deprecated Use {@link getCachedModels} / {@link fetchModels}. Kept as the seed list. */
export const AVAILABLE_MODELS = FALLBACK_MODELS;

const DEFAULT_SETTINGS: AiSettings = {
  baseUrl: DEFAULT_BASE_URL,
  apiKey: '',
  model: FALLBACK_MODELS[0].id,
  includeAliases: false,
  thinkingMode: 'fast',
};

export function getAiSettings(): AiSettings {
  const baseUrl = localStorage.getItem(`${STORAGE_PREFIX}baseUrl`) ?? DEFAULT_SETTINGS.baseUrl;
  const apiKey = localStorage.getItem(`${STORAGE_PREFIX}apiKey`) ?? DEFAULT_SETTINGS.apiKey;
  const model = normalizeModelId(localStorage.getItem(`${STORAGE_PREFIX}model`) ?? DEFAULT_SETTINGS.model);
  const includeAliases = localStorage.getItem(`${STORAGE_PREFIX}includeAliases`) === 'true';
  const thinkingMode = localStorage.getItem(`${STORAGE_PREFIX}thinkingMode`) === 'thorough' ? 'thorough' : 'fast';
  return { baseUrl, apiKey, model, includeAliases, thinkingMode };
}

export function setAiSettings(settings: Partial<AiSettings>): void {
  if (settings.baseUrl !== undefined) {
    localStorage.setItem(`${STORAGE_PREFIX}baseUrl`, settings.baseUrl);
  }
  if (settings.apiKey !== undefined) {
    localStorage.setItem(`${STORAGE_PREFIX}apiKey`, settings.apiKey);
  }
  if (settings.model !== undefined) {
    localStorage.setItem(`${STORAGE_PREFIX}model`, settings.model);
  }
  if (settings.includeAliases !== undefined) {
    localStorage.setItem(`${STORAGE_PREFIX}includeAliases`, String(settings.includeAliases));
  }
  if (settings.thinkingMode !== undefined) {
    localStorage.setItem(`${STORAGE_PREFIX}thinkingMode`, settings.thinkingMode);
  }
}

interface ModelsCache {
  at: number;
  baseUrl: string;
  models: AiModel[];
}

function readModelsCache(): ModelsCache | null {
  try {
    const raw = localStorage.getItem(MODELS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ModelsCache;
    if (!Array.isArray(parsed.models)) return null;
    // labels are derived, not trusted from storage: a list cached before the rename
    // carries the ids as labels
    return { ...parsed, models: parsed.models.map((m) => ({ id: m.id, label: modelLabel(m.id) })) };
  } catch {
    return null;
  }
}

/**
 * Returns the last-fetched model list synchronously (for instant first paint),
 * falling back to {@link FALLBACK_MODELS} when nothing is cached. The cache
 * holds the FULL list; alias visibility is applied here at read time, so
 * toggling `includeAliases` takes effect without a refetch.
 */
export function getCachedModels(): AiModel[] {
  const cache = readModelsCache();
  // The fallback list is curated aliases by definition, so the alias filter must
  // not touch it: applying it returned an EMPTY list, i.e. no models at all
  // exactly when the gateway is unreachable and the fallback is the only thing
  // standing between the user and a dead dropdown.
  if (!cache || cache.models.length === 0) return FALLBACK_MODELS;
  return applyAliasFilter(cache.models);
}

/**
 * Full model list WITHOUT the alias-visibility filter, narrowed to chat-capable
 * models — used by the Review model picker (experimentation), where the curated
 * friendly aliases (e.g. "Claude Sonnet", "GLM") are exactly what we want to
 * show. Drops embeddings/transcription/rerank models by id heuristic.
 */
export function getAllChatModels(): AiModel[] {
  const cache = readModelsCache();
  const list = cache && cache.models.length > 0 ? cache.models : FALLBACK_MODELS;
  return list.filter((m) => !/embedding|whisper|voxtral|bge|rerank/i.test(m.id));
}

/** True when the cached list is still fresh for the current base URL. */
export function isModelsCacheFresh(baseUrl: string): boolean {
  const cache = readModelsCache();
  return (
    !!cache &&
    cache.baseUrl === baseUrl &&
    Date.now() - cache.at < MODELS_CACHE_TTL_MS &&
    cache.models.length > 0
  );
}

/**
 * Fetches the available models from the gateway's OpenAI-compatible `/models`
 * endpoint and caches the result. Falls back to {@link FALLBACK_MODELS} if the
 * request fails or returns nothing. Never throws.
 */
export async function fetchModels(options: { force?: boolean } = {}): Promise<AiModel[]> {
  const { baseUrl, apiKey } = getAiSettings();

  if (!options.force && isModelsCacheFresh(baseUrl)) {
    return getCachedModels();
  }
  if (!apiKey) {
    return getCachedModels();
  }

  const { url, extraHeaders } = buildEndpoint(baseUrl, '/models');
  try {
    let response = await fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${apiKey}`, ...extraHeaders },
    });
    if (response.status === 401 || response.status === 403) {
      // Brokered key rotated server-side → silently re-provision and retry
      // once. (Dynamic import: keyBroker statically imports this module.)
      const { recoverBrokeredKey } = await import('../auth/keyBroker');
      const fresh = await recoverBrokeredKey();
      if (fresh) {
        response = await fetch(url, {
          method: 'GET',
          headers: { Authorization: `Bearer ${fresh}`, ...extraHeaders },
        });
      }
    }
    if (!response.ok) return getCachedModels();

    const data = await response.json();
    const rows: unknown[] = Array.isArray(data?.data) ? data.data : [];
    // Cache the FULL list (aliases included); visibility is decided at read time.
    const models: AiModel[] = rows
      .map((row) => (row as { id?: unknown })?.id)
      .filter((id): id is string => typeof id === 'string' && id.length > 0)
      .map((id) => ({ id, label: modelLabel(id) }));

    if (models.length === 0) return getCachedModels();

    models.sort((a, b) => a.id.localeCompare(b.id));
    const cache: ModelsCache = { at: Date.now(), baseUrl, models };
    try {
      localStorage.setItem(MODELS_CACHE_KEY, JSON.stringify(cache));
    } catch {
      /* storage full / unavailable — return live list anyway */
    }
    return applyAliasFilter(models);
  } catch {
    return getCachedModels();
  }
}
